import { noteCodeToLabel } from "./note-data.js";
import {
  effectName,
  TRACKER_EFFECT,
  TRACKER_INSTRUMENT_EMPTY,
  type ChannelEvent,
  type TrackerEffectCode
} from "./model.js";

export const TRACKER_EFFECT_OPTIONS: Array<{ code: TrackerEffectCode; label: string }> = [
  { code: TRACKER_EFFECT.none, label: "---" },
  { code: TRACKER_EFFECT.off, label: "CUT" },
  { code: TRACKER_EFFECT.arp, label: "ARP" },
  { code: TRACKER_EFFECT.por, label: "POR" },
  { code: TRACKER_EFFECT.vib, label: "VIB" },
  { code: TRACKER_EFFECT.pwm, label: "PWM" },
  { code: TRACKER_EFFECT.ret, label: "RET" },
  { code: TRACKER_EFFECT.spd, label: "SPD" },
  { code: TRACKER_EFFECT.brk, label: "BRK" },
  { code: TRACKER_EFFECT.jmp, label: "JMP" },
  { code: TRACKER_EFFECT.fct, label: "FCT" },
  { code: TRACKER_EFFECT.frs, label: "FRS" },
  { code: TRACKER_EFFECT.mvl, label: "MVL" }
];

export function formatOrderList(orderList: number[]): string {
  return orderList.map((value) => value.toString().padStart(2, "0")).join(" ");
}

export function parseOrderList(text: string, patternCount: number): number[] {
  const values = text
    .trim()
    .split(/[^0-9]+/)
    .filter((part) => part !== "")
    .map((part) => Number.parseInt(part, 10))
    .filter((value) => Number.isFinite(value))
    .map((value) => Math.max(0, Math.min(patternCount - 1, value)));
  return values.length === 0 ? [0] : values;
}

export function formatInstrumentRef(instrument: number): string {
  if (instrument === TRACKER_INSTRUMENT_EMPTY) {
    return "..";
  }
  return (instrument + 1).toString().padStart(2, "0");
}

export function formatEffectArg(value: number): string {
  return value.toString(16).toUpperCase().padStart(2, "0");
}

export type TrackerCellParts = {
  ariaLabel: string;
  effect: string;
  effectArg: string;
  instrument: string;
  note: string;
};

export function formatCellParts(event: ChannelEvent): TrackerCellParts {
  const note = noteCodeToLabel(event.note).padEnd(3, " ");
  const instrument = formatInstrumentRef(event.instrument);
  const effect = effectName(event.effect);
  const effectArg = effect === "---" ? "--" : formatEffectArg(event.effectArg);
  return {
    ariaLabel: formatCellAriaLabel(event),
    effect,
    effectArg,
    instrument,
    note
  };
}

export function formatCellAriaLabel(event: ChannelEvent): string {
  const noteLabel = noteCodeToLabel(event.note);
  const instrumentLabel = event.instrument === TRACKER_INSTRUMENT_EMPTY ? "no instrument" : `instrument ${formatInstrumentRef(event.instrument)}`;
  const effectLabel = effectName(event.effect);
  if (effectLabel === "---") {
    return `${noteLabel}, ${instrumentLabel}, no effect`;
  }
  return `${noteLabel}, ${instrumentLabel}, effect ${effectLabel} ${formatEffectArg(event.effectArg)}`;
}

export function formatCell(event: ChannelEvent): string {
  const parts = formatCellParts(event);
  return `${parts.note} ${parts.instrument} ${parts.effect}${parts.effect === "---" ? "--" : parts.effectArg}`;
}

export function formatRow(rowIndex: number): string {
  return rowIndex.toString().padStart(2, "0");
}

export function effectOptionLabel(effect: TrackerEffectCode): string {
  return TRACKER_EFFECT_OPTIONS.find((option) => option.code === effect)?.label ?? effectName(effect);
}
